import { useState, useId } from 'react';
import ScopeSelector from './ScopeSelector';
import type { ICustomAlertPreference } from '../../types/api-caller';
import PillToggle from '../PillToggle';

interface CustomAlertEditorProps {
  alert?: ICustomAlertPreference;
  mode: 'create' | 'edit';
  createDefaultName?: string;
  onSave: (alert: ICustomAlertPreference) => void;
  onCancel?: () => void;
}

const CustomAlertEditor: React.FC<CustomAlertEditorProps> = ({ alert, mode, createDefaultName, onSave, onCancel }) => {
  const [name, setName] = useState<string>(alert?.name ?? createDefaultName ?? '');
  const [frequency, setFrequency] = useState<string>((alert?.frequency ?? 'DAILY').toLowerCase());
  const [scoreIncreaseAlertEnabled, setScoreIncreaseAlertEnabled] = useState<boolean>(
    mode === 'create' ? true : alert?.increase_threshold != null
  );
  const [scoreDecreaseAlertEnabled, setScoreDecreaseAlertEnabled] = useState<boolean>(alert?.decrease_threshold != null);
  const [scoreIncreaseValue, setScoreIncreaseValue] = useState<string>(
    alert?.increase_threshold != null ? String(alert.increase_threshold) : '10'
  );
  const [scoreDecreaseValue, setScoreDecreaseValue] = useState<string>(
    alert?.decrease_threshold != null ? String(alert.decrease_threshold) : '10'
  );
  const [excludeLowArticles, setExcludeLowArticles] = useState<boolean>(alert?.exclude_low_articles ?? false);
  const [portfolioEnabled, setPortfolioEnabled] = useState<boolean>(!alert || alert.scope_type === 'PORTFOLIO');
  const [allListsEnabled, setAllListsEnabled] = useState<boolean>(alert?.scope_type === 'ALL_LISTS');
  const [selectedLists, setSelectedLists] = useState<number[]>(alert?.scope_type === 'SELECTED_LISTS' ? (alert.list_ids ?? []) : []);

  const increaseId = useId();
  const decreaseId = useId();
  const lowArticlesId = useId();

  function buildPayload(
    overrides: Partial<{
      name: string;
      frequency: string;
      scoreIncreaseAlertEnabled: boolean;
      scoreDecreaseAlertEnabled: boolean;
      scoreIncreaseValue: string;
      scoreDecreaseValue: string;
      excludeLowArticles: boolean;
      portfolioEnabled: boolean;
      allListsEnabled: boolean;
      selectedLists: number[];
    }> = {}
  ): ICustomAlertPreference {
    const nextName = overrides.name ?? name;
    const nextFrequency = overrides.frequency ?? frequency;
    const nextScoreIncreaseEnabled = overrides.scoreIncreaseAlertEnabled ?? scoreIncreaseAlertEnabled;
    const nextScoreDecreaseEnabled = overrides.scoreDecreaseAlertEnabled ?? scoreDecreaseAlertEnabled;
    const nextScoreIncreaseValue = overrides.scoreIncreaseValue ?? scoreIncreaseValue;
    const nextScoreDecreaseValue = overrides.scoreDecreaseValue ?? scoreDecreaseValue;
    const nextExcludeLowArticles = overrides.excludeLowArticles ?? excludeLowArticles;
    const nextPortfolioEnabled = overrides.portfolioEnabled ?? portfolioEnabled;
    const nextAllListsEnabled = overrides.allListsEnabled ?? allListsEnabled;
    const nextSelectedLists = overrides.selectedLists ?? selectedLists;

    let scope_type: ICustomAlertPreference['scope_type'];
    let list_ids: number[];

    if (nextPortfolioEnabled) {
      scope_type = 'PORTFOLIO';
      list_ids = [];
    } else if (nextAllListsEnabled) {
      scope_type = 'ALL_LISTS';
      list_ids = [];
    } else {
      scope_type = 'SELECTED_LISTS';
      list_ids = nextSelectedLists;
    }

    return {
      name: nextName,
      alert_id: alert?.alert_id ?? 0,
      enabled: alert?.enabled ?? true,
      increase_threshold: nextScoreIncreaseEnabled ? Number(nextScoreIncreaseValue) : null,
      decrease_threshold: nextScoreDecreaseEnabled ? Number(nextScoreDecreaseValue) : null,
      frequency: nextFrequency.toUpperCase(),
      scope_type,
      exclude_low_articles: nextExcludeLowArticles,
      list_ids,
    };
  }

  function emitChange(
    overrides: Partial<{
      name: string;
      frequency: string;
      scoreIncreaseAlertEnabled: boolean;
      scoreDecreaseAlertEnabled: boolean;
      scoreIncreaseValue: string;
      scoreDecreaseValue: string;
      excludeLowArticles: boolean;
      portfolioEnabled: boolean;
      allListsEnabled: boolean;
      selectedLists: number[];
    }> = {}
  ) {
    if (mode === 'edit') {
      onSave(buildPayload(overrides));
    }
  }

  function handleFrequencyChange(val: string) {
    setFrequency(val);
    emitChange({ frequency: val });
  }

  function handleScoreIncreaseEnabledChange(checked: boolean) {
    if (!checked && !scoreDecreaseAlertEnabled) return;
    setScoreIncreaseAlertEnabled(checked);
    emitChange({ scoreIncreaseAlertEnabled: checked });
  }

  function handleScoreDecreaseEnabledChange(checked: boolean) {
    if (!checked && !scoreIncreaseAlertEnabled) return;
    setScoreDecreaseAlertEnabled(checked);
    emitChange({ scoreDecreaseAlertEnabled: checked });
  }

  function handleScoreIncreaseValueChange(value: string) {
    if (Number(value) >= 0 && Number(value) <= 100) {
      setScoreIncreaseValue(value);
      emitChange({ scoreIncreaseValue: value });
    }
  }

  function handleScoreDecreaseValueChange(value: string) {
    if (Number(value) >= 0 && Number(value) <= 100) {
      setScoreDecreaseValue(value);
      emitChange({ scoreDecreaseValue: value });
    }
  }

  function handleExcludeLowArticlesChange(checked: boolean) {
    setExcludeLowArticles(checked);
    emitChange({ excludeLowArticles: checked });
  }

  function handlePortfolioChange(checked: boolean) {
    if (!checked && !allListsEnabled && selectedLists.length === 0) return;

    const nextAllListsEnabled = checked ? false : allListsEnabled;
    const nextSelectedLists = checked ? [] : selectedLists;

    setPortfolioEnabled(checked);
    if (checked) {
      setAllListsEnabled(false);
      setSelectedLists([]);
    }

    emitChange({
      portfolioEnabled: checked,
      allListsEnabled: nextAllListsEnabled,
      selectedLists: nextSelectedLists,
    });
  }

  function handleAllListsChange(checked: boolean) {
    if (!checked && !portfolioEnabled && selectedLists.length === 0) return;

    const nextPortfolioEnabled = checked ? false : portfolioEnabled;
    const nextSelectedLists = checked ? [] : selectedLists;

    setAllListsEnabled(checked);
    if (checked) {
      setSelectedLists([]);
      setPortfolioEnabled(false);
    }

    emitChange({
      portfolioEnabled: nextPortfolioEnabled,
      allListsEnabled: checked,
      selectedLists: nextSelectedLists,
    });
  }

  function handleListsChange(listId: number, checked: boolean, allListsChecked: boolean) {
    let nextPortfolioEnabled = portfolioEnabled;
    let nextAllListsEnabled = allListsEnabled;
    let nextSelectedLists = selectedLists;

    if (allListsEnabled && !portfolioEnabled) {
      nextAllListsEnabled = false;
      nextSelectedLists = [listId];
      nextPortfolioEnabled = false;
    } else {
      if (checked) {
        if (allListsChecked) {
          nextAllListsEnabled = true;
          nextSelectedLists = [];
          nextPortfolioEnabled = false;
        } else {
          nextSelectedLists = [...selectedLists, listId];
          nextPortfolioEnabled = false;
          nextAllListsEnabled = false;
        }
      } else {
        nextSelectedLists = selectedLists.filter((l) => l !== listId);
      }
    }

    setAllListsEnabled(nextAllListsEnabled);
    setSelectedLists(nextSelectedLists);
    setPortfolioEnabled(nextPortfolioEnabled);

    emitChange({
      portfolioEnabled: nextPortfolioEnabled,
      allListsEnabled: nextAllListsEnabled,
      selectedLists: nextSelectedLists,
    });
  }

  function handleNameChange(value: string) {
    setName(value);
    emitChange({ name: value });
  }

  function handleSaveClick() {
    if (mode === 'create' && name.trim() && (scoreIncreaseAlertEnabled || scoreDecreaseAlertEnabled)) {
      onSave(buildPayload());
    }
  }

  return (
    <div className="flex flex-col gap-2 py-2">
      <div className="flex flex-row gap-4 items-center">
        <input
          type="text"
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="Alert name"
          className="flex-1 border border-gray-300 rounded px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ow-blue"
        />
      </div>

      <div className="flex flex-row gap-2 justify-end items-start">
        <div className="flex-1 flex flex-col gap-2">
          <PillToggle
            options={[
              { label: 'Daily', value: 'daily' },
              { label: 'Weekly', value: 'weekly' },
            ]}
            selected={frequency.toLowerCase()}
            onChange={handleFrequencyChange}
          />

          <div className="flex gap-3 items-center">
            <input
              id={increaseId}
              type="checkbox"
              checked={scoreIncreaseAlertEnabled}
              onChange={(e) => handleScoreIncreaseEnabledChange(e.target.checked)}
              className="accent-ow-blue w-5 h-5 rounded border-gray-300 cursor-pointer"
            />
            <label htmlFor={increaseId} className="font-medium text-base text-black cursor-pointer self-center">
              if FSS score increases by
            </label>
            <input
              type="number"
              min={0}
              max={100}
              className="w-16 border border-gray-300 rounded px-2 py-1 text-base"
              value={scoreIncreaseValue}
              onChange={(e) => handleScoreIncreaseValueChange(e.target.value)}
              placeholder="10"
            />
            <span className="text-base text-black self-center">%</span>
          </div>

          <div className="flex gap-3 items-center">
            <input
              id={decreaseId}
              type="checkbox"
              checked={scoreDecreaseAlertEnabled}
              onChange={(e) => handleScoreDecreaseEnabledChange(e.target.checked)}
              className="accent-ow-blue w-5 h-5 rounded border-gray-300 cursor-pointer"
            />
            <label htmlFor={decreaseId} className="font-medium text-base text-black cursor-pointer self-center">
              if FSS score decreases by
            </label>
            <input
              type="number"
              min={0}
              max={100}
              className="w-16 border border-gray-300 rounded px-2 py-1 text-base"
              value={scoreDecreaseValue}
              onChange={(e) => handleScoreDecreaseValueChange(e.target.value)}
              placeholder="10"
            />
            <span className="text-base text-black self-center">%</span>
          </div>

          <div className="grid grid-cols-[auto_1fr] grid-rows-[auto_auto] gap-x-3 items-center">
            <input
              id={lowArticlesId}
              type="checkbox"
              checked={excludeLowArticles}
              onChange={(e) => handleExcludeLowArticlesChange(e.target.checked)}
              className="accent-ow-blue w-5 h-5 rounded border-gray-300 cursor-pointer row-span-1"
            />
            <label htmlFor={lowArticlesId} className="font-medium text-base text-black cursor-pointer self-center">
              Remove companies with 'low article' scores from emails
            </label>
            <div />
            <p className="text-gray-400 text-sm col-start-2 pr-8 pt-1">
              This will remove companies from your daily/weekly emails that have 'low article' volumes, which may dominate top 20 email
              notifications because they can have bigger swings in percentage score changes.
            </p>
          </div>
        </div>

        <div className="flex-1 flex flex-col gap-2">
          <ScopeSelector
            portfolioEnabled={portfolioEnabled}
            allListsEnabled={allListsEnabled}
            selectedLists={selectedLists}
            onPortfolioChange={handlePortfolioChange}
            onAllListsChange={handleAllListsChange}
            onListsChange={handleListsChange}
            sectionId={`custom-editor-${alert?.alert_id ?? 'new'}`}
          />
        </div>
      </div>

      {mode === 'create' && (
        <div className="flex flex-row gap-2 mt-2">
          <button
            onClick={handleSaveClick}
            disabled={!name.trim() || (!scoreIncreaseAlertEnabled && !scoreDecreaseAlertEnabled)}
            className="bg-ow-blue text-white font-medium px-6 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
          >
            Save
          </button>
          {onCancel && (
            <button onClick={onCancel} className="bg-gray-300 text-black font-medium px-6 py-2 rounded hover:bg-gray-400 transition-colors">
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomAlertEditor;
